// Real Jacket API — calls /api/V1/Jacket/* (Phase 3). Signatures mirror
// jacketMock.ts so api/index.ts swaps mock ↔ real with one switch.

import { postJson } from './http';
import type {
  AgentInfo,
  CplPrefill,
  Endorsement,
  JacketDetail,
  JacketFormTypes,
  JacketFormValues,
  JacketSummary,
  LenderSuggestion,
  PolicyType,
  SaveResult,
} from '@/types/api';

const API = '/api/V1';

export const jacketRealApi = {
  async getPolicyTypes(): Promise<PolicyType[]> {
    const res = await postJson<{ err: string; data?: PolicyType[] }>(`${API}/Jacket/PolicyTypes`);
    return res.err === '1' && Array.isArray(res.data) ? res.data : [];
  },
  async getJacketPropertyTypes(): Promise<string[]> {
    const res = await postJson<{ err: string; data?: string[] }>(`${API}/Jacket/PropertyTypes`);
    return res.err === '1' && Array.isArray(res.data) ? res.data : [];
  },
  async getJacketFormTypes(): Promise<JacketFormTypes> {
    const res = await postJson<{ err: string; data?: JacketFormTypes }>(`${API}/Jacket/FormTypes`);
    return res.err === '1' && res.data ? res.data : { LM: [], LO: [], CO: [] };
  },
  async getJacketEndorsements(): Promise<Endorsement[]> {
    const res = await postJson<{ err: string; data?: Endorsement[] }>(`${API}/Jacket/Endorsements`);
    return res.err === '1' && Array.isArray(res.data) ? res.data : [];
  },
  async getJacketCounties(): Promise<string[]> {
    const res = await postJson<{ err: string; data?: string[] }>(`${API}/Jacket/Counties`);
    return res.err === '1' && Array.isArray(res.data) ? res.data : [];
  },
  async getJacketAgent(): Promise<AgentInfo> {
    const res = await postJson<{ err: string; data?: AgentInfo }>(`${API}/Jacket/Agent`);
    if (res.err !== '1' || !res.data) throw new Error('Agent lookup failed');
    return res.data;
  },
  async getLenders(term: string): Promise<LenderSuggestion[]> {
    const res = await postJson<{ err: string; data?: LenderSuggestion[] }>(`${API}/Jacket/Lenders`, { term });
    return res.err === '1' && Array.isArray(res.data) ? res.data : [];
  },
  async getJacketPropertyByFile(fileNumber: string): Promise<CplPrefill | null> {
    const res = await postJson<{ err: string; data?: CplPrefill }>(`${API}/Jacket/PropertyByFile`, { fileNumber });
    return res.err === '1' && res.data ? res.data : null;
  },
  async getJacketPremium(amount: number): Promise<number> {
    const res = await postJson<{ err: string; premium?: number }>(`${API}/Jacket/Premium`, { amount });
    return res.err === '1' && typeof res.premium === 'number' ? res.premium : 0;
  },
  async listJackets(params: { from?: string; to?: string; filter?: string; over300k?: boolean } = {}): Promise<JacketSummary[]> {
    const res = await postJson<{ err: string; data?: JacketSummary[] }>(`${API}/Jacket/List`, { ...params });
    return res.err === '1' && Array.isArray(res.data) ? res.data : [];
  },
  async getJacket(id: number): Promise<JacketDetail | null> {
    const res = await postJson<{ err: string; data?: JacketDetail }>(`${API}/Jacket/Get`, { id });
    return res.err === '1' && res.data ? res.data : null;
  },
  async createJacket(values: JacketFormValues): Promise<SaveResult> {
    return postJson<SaveResult>(`${API}/Jacket/Create`, { ...values });
  },
  async updateJacket(id: number, values: JacketFormValues): Promise<SaveResult> {
    return postJson<SaveResult>(`${API}/Jacket/Edit`, { id, ...values });
  },
  async voidJacket(id: number, reason: string): Promise<SaveResult> {
    return postJson<SaveResult>(`${API}/Jacket/Void`, { id, reason });
  },
};
